import React, { useState } from 'react';
import I18n from 'i18n-js';
import Currency from '@reverbdotcom/commons/src/currency';

import { RCSwitch, RCCheckbox, RCTextInput } from '@reverbdotcom/cadence/components';

export const MINIMUM_AUTO_REJECT_THRESHOLD = 50;
export const MAXIMUM_AUTO_REJECT_THRESHOLD = 80;

export const MINIMUM_AUTO_ACCEPT_THRESHOLD = 51;
export const MAXIMUM_AUTO_ACCEPT_THRESHOLD = 99;


export interface IProps {
  brandNewSelected: boolean;
  bStockSelected: boolean;
  usedSelected: boolean;
  autoRejectThreshold: string | null;
  autoAcceptThreshold: string | null;
  minPriceAmount: string | null;
  maxPriceAmount: string | null;
  haggleProtection: boolean;
  autoCounter: boolean;
  currency: string;
}

export default function SellingPoliciesOfferBot(props: IProps) {
  const [offerBotEnabled, setOfferBotEnabled] = useState(props.brandNewSelected || props.bStockSelected || props.usedSelected);
  const [brandNewChecked, setBrandNewChecked] = useState(props.brandNewSelected);
  const [bStockChecked, setBStockChecked] = useState(props.bStockSelected);
  const [usedChecked, setUsedChecked] = useState(props.usedSelected);
  const [minPriceAmount, setMinPriceAmount] = useState(props.minPriceAmount);
  const [maxPriceAmount, setMaxPriceAmount] = useState(props.maxPriceAmount);
  const [autoRejectThreshold, setAutoRejectThreshold] = useState(props.autoRejectThreshold);
  const [autoAcceptThreshold, setAutoAcceptThreshold] = useState(props.autoAcceptThreshold);
  const [autoRejectChecked, setRejectDeclineChecked] = useState(!!props.autoRejectThreshold);
  const [haggleProtectionChecked, setHaggleProtectionChecked] = useState(props.haggleProtection);
  const [autoCounterChecked, setAutoCounterChecked] = useState(props.autoCounter);

  const currencySymbol = () => {
    return Currency.get(props.currency).symbol;
  };

  const autoThresholdErrorMessage = (value, minimum, maximum) => {
    if (value) {
      const parsedThreshold = parseInt(value);
      if (parsedThreshold > maximum || parsedThreshold < minimum) {
        return I18n.t('discovery.shopSettings.offerBotPolicy.autoThresholdPercentageError', { maximum: maximum, minimum: minimum });
      }
    }

    return null;
  };

  return (
    <>
      <div className="g-container">
        <div className="g-col-12">
          <RCSwitch
            checked={offerBotEnabled}
            id="offerBotEnabled"
            name="offerBotEnabled"
            label= {I18n.t('discovery.shopSettings.offerBotPolicy.automaticAcceptLabel')}
            onChange= {() => setOfferBotEnabled(!offerBotEnabled)}
          />
        </div>
      </div>
      {offerBotEnabled && (
        <div className="g-container">
          <div className="g-col-12 pt-2">
            <div className="g-col-12 pt-1">
              <div className="weight-bold mb-0">
                {I18n.t('discovery.shopSettings.offerBotPolicy.conditionsTitle')}
              </div>
            </div>
            <div className="g-col-12 pt-1">
              <RCCheckbox
                name="brandNewChecked"
                id="brandNewChecked"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.conditionBrandNewOption')}
                onChange={(e) => setBrandNewChecked(e.target.checked)}
                checked={brandNewChecked}
              />
            </div>
            <div className="g-col-12 pt-1">
              <RCCheckbox
                name="bStockChecked"
                id="bStockChecked"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.conditionBStockOption')}
                onChange={(e) => setBStockChecked(e.target.checked)}
                checked={bStockChecked}
              />
            </div>
            <div className="g-col-12 pt-1">
              <RCCheckbox
                name="usedChecked"
                id="usedChecked"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.conditionUsedOption')}
                onChange={(e) => setUsedChecked(e.target.checked)}
                checked={usedChecked}
              />
            </div>
          </div>
          <div className="g-col-12 pt-2">
            <div className="g-col-12 pt-1">
              <div className="weight-bold mb-0">
                {I18n.t('discovery.shopSettings.offerBotPolicy.boundariesTitle')}
              </div>
            </div>
            <div className="g-col-10 pt-1">
              <p>{I18n.t('discovery.shopSettings.offerBotPolicy.boundariesDescription')}</p>
            </div>
            <div className="g-col-4 g-col-mobile-12">
              <RCTextInput
                name="shop_policies_form[offer_bot_rules][min_price_amount]"
                id="shop_policies_form_offer_bot_rules_min_price_amount"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.boundariesLowTitle')}
                maxLength={21}
                onChange={(e) => setMinPriceAmount(e.target.value)}
                prefixText={currencySymbol()}
                inputMode="decimal"
                type="number"
                value={minPriceAmount}
              />
            </div>
            <div className="g-col-4 g-col-mobile-12">
              <RCTextInput
                name="shop_policies_form[offer_bot_rules][max_price_amount]"
                id="shop_policies_form_offer_bot_rules_max_price_amount"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.boundariesHighTitle')}
                maxLength={21}
                onChange={(e) => setMaxPriceAmount(e.target.value)}
                prefixText={currencySymbol()}
                inputMode="decimal"
                type="number"
                value={maxPriceAmount}
              />
            </div>
          </div>
          <div className="g-col-10 pt-2">
            <div className="g-col-12 pt-1">
              <div className="weight-bold mb-0">
                {I18n.t('discovery.shopSettings.offerBotPolicy.autoPercentageDescription')}
              </div>
            </div>
            <div className="g-col-10 g-col-mobile-12 pt-1">
              <p>{I18n.t('discovery.shopSettings.offerBotPolicy.autoPercentageExample')}</p>
            </div>
            <div className="g-col-4 g-col-mobile-12">
              <RCTextInput
                name="shop_policies_form[offer_bot_rules][auto_accept_threshold]"
                id="shop_policies_form_offer_bot_rules_auto_accept_threshold"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.autoPercentageLabel')}
                maxLength={2}
                onChange={(e) => setAutoAcceptThreshold(e.target.value)}
                suffixText="%"
                inputMode="numeric"
                type="number"
                value={autoAcceptThreshold}
                errorText={autoThresholdErrorMessage(autoAcceptThreshold, MINIMUM_AUTO_ACCEPT_THRESHOLD, MAXIMUM_AUTO_ACCEPT_THRESHOLD)}
              />
            </div>
            <div className="g-col-12 pt-1">
              <div className="weight-bold mb-1 mt-1">
                {I18n.t('discovery.shopSettings.offerBotPolicy.autoCounterQuestion')}
              </div>
            </div>
            <div className="g-col-12 pt-1">
              <RCCheckbox
                name="autoCounterChecked"
                id="autoCounterChecked"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.autoCounter')}
                onChange={(e) => setAutoCounterChecked(e.target.checked)}
                checked={autoCounterChecked}
              />
            </div>
          </div>
          <div className="g-col-12 pt-2">
            <div className="g-col-12 pt-1">
              <div className="weight-bold mb-0">
                {I18n.t('discovery.shopSettings.offerBotPolicy.autoDeclineQuestion')}
              </div>
            </div>
            <div className="g-col-12 pt-1">
              <RCCheckbox
                id="autoRejectChecked"
                name="autoRejectChecked"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.autoDeclineCheckboxLabel')}
                sublabel={I18n.t('discovery.shopSettings.offerBotPolicy.autoDeclineCheckboxSublabel')}
                onChange={(e) => setRejectDeclineChecked(e.target.checked)}
                checked={autoRejectChecked}
              />
            </div>
            {autoRejectChecked && (
              <div className="g-col-4 g-col-mobile-12 pt-1 plr-3">
                <RCTextInput
                  name="shop_policies_form[offer_bot_rules][auto_reject_threshold]"
                  id="shop_policies_form_offer_bot_rules_auto_reject_threshold"
                  label={I18n.t('discovery.shopSettings.offerBotPolicy.lowballPercentageTitle')}
                  maxLength={2}
                  onChange={(e) => setAutoRejectThreshold(e.target.value)}
                  suffixText="%"
                  inputMode="numeric"
                  type="number"
                  value={autoRejectThreshold}
                  errorText={autoThresholdErrorMessage(autoRejectThreshold, MINIMUM_AUTO_REJECT_THRESHOLD, MAXIMUM_AUTO_REJECT_THRESHOLD)}
                />
              </div>
            )}
          </div>
          <div className="g-col-12 pt-2">
            <div className="g-col-12 pt-1">
              <div className="weight-bold mb-0">
                {I18n.t('discovery.shopSettings.offerBotPolicy.haggleProtectionQuestion')}
              </div>
            </div>
            <div className="g-col-12 pt-1">
              <RCCheckbox
                name="haggleProtectionChecked"
                id="haggleProtectionChecked"
                label={I18n.t('discovery.shopSettings.offerBotPolicy.haggleProtection')}
                onChange={(e) => setHaggleProtectionChecked(e.target.checked)}
                checked={haggleProtectionChecked}
              />
            </div>
          </div>
        </div>
      )}
      <input
        type="hidden"
        name="shop_policies_form[offer_bot_rules][offer_bot_enabled]"
        id="shop_policies_form_offer_bot_rules_offer_bot_enabled"
        value={offerBotEnabled && (brandNewChecked || bStockChecked || usedChecked) ? '1' : '0'}
      />
      <input
        type="hidden"
        name="shop_policies_form[offer_bot_rules][brand_new_selected]"
        id="shop_policies_form_offer_bot_rules_brand_new_selected"
        value={brandNewChecked ? '1' : '0'}
      />
      <input
        type="hidden"
        name="shop_policies_form[offer_bot_rules][b_stock_selected]"
        id="shop_policies_form_offer_bot_rules_b_stock_selected"
        value={bStockChecked ? '1' : '0'}
      />
      <input
        type="hidden"
        name="shop_policies_form[offer_bot_rules][used_selected]"
        id="shop_policies_form_offer_bot_rules_used_selected"
        value={usedChecked ? '1' : '0'}
      />
      <input
        name="shop_policies_form[offer_bot_rules][haggle_protection]"
        id="shop_policies_form_offer_bot_rules_haggle_protection"
        type="hidden"
        value={haggleProtectionChecked ? '1' : '0'}
      />
      <input
        name="shop_policies_form[offer_bot_rules][auto_counter]"
        id="shop_policies_form_offer_bot_rules_auto_counter"
        type="hidden"
        value={autoCounterChecked ? '1' : '0'}
      />
      <input
        name="shop_policies_form[offer_bot_rules][currency]"
        id="shop_policies_form_offer_bot_rules_currency"
        type="hidden"
        value={props.currency}
      />
    </>
  );
}
